import React from 'react';
import { AircraftType, Quote, UserAccount } from '../../types';
import { TacticalMission } from './types';
import PlanningWeatherNode from './PlanningWeatherNode';
import { getVerificationLabel } from '../../utils/inboundLabels';
import { PlannerRoute, PlannerRouteStopUpdateInput, plannerService } from '../../services/plannerService';

const inboundBadgeClass = (tone: 'source' | 'reply-ok' | 'reply-pending' | 'verify-ok' | 'verify-warn' | 'case-open' | 'case-waiting' | 'case-closed') => {
  if (tone === 'source') return 'border-cyan-200 bg-cyan-50 text-cyan-700';
  if (tone === 'reply-ok') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (tone === 'reply-pending') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (tone === 'verify-ok') return 'border-sky-200 bg-sky-50 text-sky-700';
  if (tone === 'verify-warn') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (tone === 'case-waiting') return 'border-indigo-200 bg-indigo-50 text-indigo-700';
  if (tone === 'case-closed') return 'border-slate-200 bg-slate-100 text-slate-700';
  return 'border-rose-200 bg-rose-50 text-rose-700';
};

const renderInboundHubBadges = (mission: TacticalMission, compact = false) => {
  const inbound = mission.inboundHub;
  if (!inbound || String(inbound.source || '').toUpperCase() !== 'EMAIL_INTAKE') return null;

  const verification = String(inbound.verificationStatus || 'UNVERIFIED').toUpperCase();
  const caseState = String(inbound.caseState || 'OPEN').toUpperCase();
  const replyTone = inbound.repliedAt ? 'reply-ok' : 'reply-pending';
  const verificationTone = verification === 'VERIFIED' || verification === 'TRUSTED' ? 'verify-ok' : 'verify-warn';
  const caseTone =
    caseState === 'WAITING_CLIENT'
      ? 'case-waiting'
      : caseState === 'RESOLVED_WON' || caseState === 'RESOLVED_LOST'
      ? 'case-closed'
      : 'case-open';

  const wrapClass = compact ? 'mt-2 flex flex-wrap gap-1' : 'mt-2 flex flex-wrap gap-1.5';
  const pillClass = compact
    ? 'rounded-full border px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.12em]'
    : 'rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em]';

  return (
    <div className={wrapClass}>
      <span className={`${pillClass} ${inboundBadgeClass('source')}`}>Inbound Hub</span>
      <span className={`${pillClass} ${inboundBadgeClass(replyTone)}`}>{inbound.repliedAt ? 'Ack Sent' : 'Awaiting Reply'}</span>
      <span className={`${pillClass} ${inboundBadgeClass(verificationTone)}`}>{getVerificationLabel(verification)}</span>
      <span className={`${pillClass} ${inboundBadgeClass(caseTone)}`}>
        {caseState === 'WAITING_CLIENT'
          ? 'Waiting Client'
          : caseState === 'RESOLVED_WON'
          ? 'Closed Won'
          : caseState === 'RESOLVED_LOST'
          ? 'Closed Lost'
          : 'Open Case'}
      </span>
    </div>
  );
};

interface PlanningExecutionBoardProps {
  selectedDate: string;
  viewMode: 'TACTICAL' | 'CREW' | 'GROUND' | 'ROUTE';
  isDFO: boolean;
  isFlightPlanner: boolean;
  draggedMissionId: string | null;
  draggedPilotId: string | null;
  crewLoading: boolean;
  dutyFilteredCrew: UserAccount[];
  crewDutyMap: Record<string, { status: 'ON_DUTY' | 'STANDBY' | 'OFF'; duties: any[] }>;
  charterReservoir: TacticalMission[];
  scheduledReservoir: TacticalMission[];
  fleetSchedule: Record<string, TacticalMission[]>;
  aircrafts: AircraftType[];
  pilots: UserAccount[];
  crewLegErrors: Record<string, string>;
  showFuelModal: string | null;
  fuelLiters: number;
  onSetShowFuelModal: (value: string | null) => void;
  onSetFuelLiters: (value: number) => void;
  onDragMissionStart: (source: 'CHARTER' | 'SCHEDULED', qId: string, lId: string) => void;
  onDragPilotStart: (id: string) => void;
  onDropOnReservoir: () => void;
  onDropOnAircraft: (tail: string) => void;
  onAssignMissionToAircraft: (source: 'CHARTER' | 'SCHEDULED', quoteId: string, legId: string, targetTail: string) => void;
  onDropPilotOnLeg: (quoteId: string, legId: string) => void;
  onOpenPaxModal: (mission: TacticalMission) => void;
  onUnassignPilot: (pilotId: string, quoteId: string, legId: string) => void;
  onUpdateQuote?: (quoteId: string, updates: Partial<Quote>) => void;
  onUnassignScheduledLeg: (bookingId: string) => void;
  onHandleFuelCommit: () => void;
  plannerRoutesByTail?: Record<string, PlannerRoute>;
  routeSavingTail?: string | null;
  onSaveGeneratedRouteForTail?: (tailNumber: string) => Promise<void> | void;
  onSaveManualRouteEdits?: (
    routeId: string,
    payload: {
      stops: PlannerRouteStopUpdateInput[];
      warnings?: any;
      diagnostics?: any;
      summaryPath?: string;
      maxOnboardPax?: number;
    }
  ) => Promise<boolean>;
  onLockRoute?: (routeId: string) => Promise<boolean>;
  onConfirmRoute?: (routeId: string) => Promise<boolean>;
  onResetRoute?: (routeId: string) => Promise<boolean>;
  getCrewRoleCode: (userId: string) => string;
}

const PlanningExecutionBoard: React.FC<PlanningExecutionBoardProps> = ({
  selectedDate,
  viewMode,
  isDFO,
  isFlightPlanner,
  draggedMissionId,
  draggedPilotId,
  crewLoading,
  dutyFilteredCrew,
  crewDutyMap,
  charterReservoir,
  scheduledReservoir,
  fleetSchedule,
  aircrafts,
  pilots,
  crewLegErrors,
  showFuelModal,
  fuelLiters,
  onSetShowFuelModal,
  onSetFuelLiters,
  onDragMissionStart,
  onDragPilotStart,
  onDropOnReservoir,
  onDropOnAircraft,
  onAssignMissionToAircraft,
  onDropPilotOnLeg,
  onOpenPaxModal,
  onUnassignPilot,
  onUpdateQuote,
  onUnassignScheduledLeg,
  onHandleFuelCommit,
  plannerRoutesByTail = {},
  routeSavingTail = null,
  onSaveGeneratedRouteForTail,
  onSaveManualRouteEdits,
  onLockRoute,
  onConfirmRoute,
  onResetRoute,
  getCrewRoleCode,
}) => {
  const [poolTab, setPoolTab] = React.useState<'ALL' | 'CHARTER' | 'SCHEDULED'>('ALL');
  const [queueQuery, setQueueQuery] = React.useState('');
  const [tailViewMode, setTailViewMode] = React.useState<'FOCUS' | 'GRID'>('FOCUS');
  const [queueCollapsed, setQueueCollapsed] = React.useState(false);
  const [selectedQueueKeys, setSelectedQueueKeys] = React.useState<string[]>([]);
  const [tailSortBy, setTailSortBy] = React.useState<'ETD' | 'CLIENT' | 'ROUTE'>('ETD');
  const [tailSortDir, setTailSortDir] = React.useState<'ASC' | 'DESC'>('ASC');
  const aircraftRailRef = React.useRef<HTMLDivElement | null>(null);
  const tailColumnRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const [selectedTail, setSelectedTail] = React.useState<string>('');
  const queueMissions = React.useMemo(() => {
    const base = poolTab === 'CHARTER'
      ? charterReservoir
      : poolTab === 'SCHEDULED'
      ? scheduledReservoir
      : [...charterReservoir, ...scheduledReservoir];
    const q = queueQuery.trim().toLowerCase();
    if (!q) return base;
    return base.filter((m) => {
      const hay = [m.customerName, m.from, m.to, m.bookingId, m.quoteId, m.productMode].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [poolTab, charterReservoir, scheduledReservoir, queueQuery]);
  const visibleQueueKeys = React.useMemo(
    () => queueMissions.map((m) => `${m.source}|${m.quoteId}|${m.id}`),
    [queueMissions]
  );

  React.useEffect(() => {
    const validKeys = new Set(queueMissions.map((m) => `${m.source}|${m.quoteId}|${m.id}`));
    setSelectedQueueKeys((prev) => prev.filter((k) => validKeys.has(k)));
  }, [queueMissions]);
  const tailEntries = React.useMemo(
    () => aircrafts.map((a) => ({ tail: a.tailNumber, missions: fleetSchedule[a.tailNumber] || [] })),
    [aircrafts, fleetSchedule]
  );
  const orderedTails = React.useMemo(() => tailEntries.map((e) => e.tail), [tailEntries]);
  const displayedTailEntries = React.useMemo(() => {
    if (tailViewMode === 'FOCUS' && selectedTail) {
      return tailEntries.filter((e) => e.tail === selectedTail);
    }
    return tailEntries;
  }, [tailEntries, tailViewMode, selectedTail]);
  const selectedTailEntry = React.useMemo(
    () => tailEntries.find((e) => e.tail === selectedTail) || null,
    [tailEntries, selectedTail]
  );

  React.useEffect(() => {
    if (!selectedTail && orderedTails.length > 0) {
      setSelectedTail(orderedTails[0]);
      return;
    }
    if (selectedTail && !orderedTails.includes(selectedTail)) {
      setSelectedTail(orderedTails[0] || '');
    }
  }, [orderedTails, selectedTail]);

  const jumpToTail = (tail: string) => {
    setSelectedTail(tail);
    const node = tailColumnRefs.current[tail];
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  const toggleQueueSelection = (mission: TacticalMission) => {
    const key = `${mission.source}|${mission.quoteId}|${mission.id}`;
    setSelectedQueueKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const resolveMissionAssignmentId = (mission: TacticalMission): string => {
    if (mission.source !== 'SCHEDULED') return mission.id;
    return String(mission.bookingId || mission.id);
  };

  const handleSelectAllVisible = () => {
    setSelectedQueueKeys(visibleQueueKeys);
  };

  const handleClearSelection = () => {
    setSelectedQueueKeys([]);
  };

  const handleBatchAssignToSelectedTail = () => {
    if (!selectedTail || selectedQueueKeys.length === 0) return;
    const byKey = new Map<string, TacticalMission>(
      queueMissions.map((m) => [`${m.source}|${m.quoteId}|${m.id}`, m] as [string, TacticalMission])
    );
    const charterDedup = new Set<string>();
    selectedQueueKeys.forEach((key) => {
      const mission = byKey.get(key);
      if (!mission) return;
      if (mission.source === 'CHARTER') {
        const dedupKey = `CHARTER:${mission.quoteId}`;
        if (charterDedup.has(dedupKey)) return;
        charterDedup.add(dedupKey);
      }
      onAssignMissionToAircraft(mission.source, mission.quoteId, resolveMissionAssignmentId(mission), selectedTail);
    });
    setSelectedQueueKeys([]);
    setQueueCollapsed(true);
  };

  const pickBestTailForMission = (mission: TacticalMission): string | null => {
    if (!orderedTails.length) return null;
    let bestTail: string | null = null;
    let bestScore = Number.NEGATIVE_INFINITY;
    orderedTails.forEach((tail) => {
      const group = fleetSchedule[tail] || [];
      const loadPenalty = group.length * 6;
      const exactRouteMatch = group.some((g) => g.from === mission.from && g.to === mission.to) ? 40 : 0;
      const sameOriginFlow = group.some((g) => g.to === mission.from) ? 18 : 0;
      const sameDestinationFlow = group.some((g) => g.from === mission.to) ? 8 : 0;
      const idleBonus = group.length === 0 ? 12 : 0;
      const score = exactRouteMatch + sameOriginFlow + sameDestinationFlow + idleBonus - loadPenalty;
      if (score > bestScore) {
        bestScore = score;
        bestTail = tail;
      }
    });
    return bestTail;
  };

  const handleBatchAssignToBestTail = () => {
    if (selectedQueueKeys.length === 0) return;
    const byKey = new Map<string, TacticalMission>(
      queueMissions.map((m) => [`${m.source}|${m.quoteId}|${m.id}`, m] as [string, TacticalMission])
    );
    const charterDedup = new Set<string>();
    selectedQueueKeys.forEach((key) => {
      const mission = byKey.get(key);
      if (!mission) return;
      if (mission.source === 'CHARTER') {
        const dedupKey = `CHARTER:${mission.quoteId}`;
        if (charterDedup.has(dedupKey)) return;
        charterDedup.add(dedupKey);
      }
      const bestTail = pickBestTailForMission(mission);
      if (!bestTail) return;
      onAssignMissionToAircraft(mission.source, mission.quoteId, resolveMissionAssignmentId(mission), bestTail);
      setSelectedTail(bestTail);
    });
    setSelectedQueueKeys([]);
    setQueueCollapsed(true);
  };

  const sortMissions = React.useCallback((missions: TacticalMission[]) => {
    const ordered = [...missions];
    ordered.sort((a, b) => {
      let cmp = 0;
      if (tailSortBy === 'ETD') {
        cmp = String(a.etd || '').localeCompare(String(b.etd || ''));
      } else if (tailSortBy === 'CLIENT') {
        cmp = String(a.customerName || '').localeCompare(String(b.customerName || ''));
      } else {
        const aRoute = `${a.from || ''}-${a.to || ''}`;
        const bRoute = `${b.from || ''}-${b.to || ''}`;
        cmp = aRoute.localeCompare(bRoute);
      }
      if (cmp === 0) cmp = String(a.id || '').localeCompare(String(b.id || ''));
      return tailSortDir === 'ASC' ? cmp : -cmp;
    });
    return ordered;
  }, [tailSortBy, tailSortDir]);

  const collapseScheduledOpsLegs = React.useCallback((missions: TacticalMission[]) => {
    if (viewMode !== 'CREW') return missions;
    const grouped = new Map<string, TacticalMission>();
    const ordered: TacticalMission[] = [];
    missions.forEach((mission) => {
      if (mission.source !== 'SCHEDULED') {
        ordered.push(mission);
        return;
      }
      const key = [
        String(mission.date || ''),
        String(mission.tailNumber || ''),
        String(mission.from || ''),
        String(mission.to || ''),
        String(mission.etd || ''),
        String(mission.eta || ''),
      ].join('|');
      const existing = grouped.get(key);
      if (!existing) {
        const seed = { ...mission };
        grouped.set(key, seed);
        ordered.push(seed);
        return;
      }
      existing.paxCount = Number(existing.paxCount || 0) + Number(mission.paxCount || 0);
      const crew = new Set<string>([...(existing.assignedCrew || []), ...(mission.assignedCrew || [])]);
      existing.assignedCrew = Array.from(crew);
      const existingRef = String(existing.bookingId || '');
      const nextRef = String(mission.bookingId || '');
      if (existingRef && nextRef && existingRef !== nextRef && !existingRef.includes('+')) {
        existing.bookingId = `${existingRef} +`;
      }
    });
    return ordered;
  }, [viewMode]);

  type RoutePreviewStop = {
    sequence: number;
    location: string;
    arrivalTime: string;
    departureTime: string;
    boarding: number;
    dropping: number;
    onboardAfter: number;
    linkedSectorKeys: string[];
    notes?: string;
    manual?: boolean;
  };

  type RoutePreviewCard = {
    tail: string;
    sectorCount: number;
    maxOnboard: number;
    warnings: string[];
    summary: string;
    stops: RoutePreviewStop[];
    sourceKind?: 'persisted' | 'preview';
    persistedStatus?: string;
    routeId?: string;
    diagnostics?: any;
    sectors?: Array<{
      sectorKey: string;
      from: string;
      to: string;
      pax: number;
      etd?: string;
      eta?: string;
      bookingId?: string | null;
      missionId?: string | null;
      isSynthetic?: boolean;
    }>;
  };

  type RouteTailEditState = {
    stops: Array<Omit<RoutePreviewStop, 'sequence' | 'onboardAfter'> & { sequence?: number; onboardAfter?: number }>;
    orderEdited?: boolean;
  };

  const generatedRoutePreviewByTail = React.useMemo<RoutePreviewCard[]>(() => {
    const parseClock = (raw?: string | null): number | null => {
      const value = String(raw || '').trim();
      const m = /^(\d{1,2}):(\d{2})$/.exec(value);
      if (!m) return null;
      const hh = Number(m[1]);
      const mm = Number(m[2]);
      if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
      return hh * 60 + mm;
    };

    return tailEntries.map(({ tail, missions }) => {
      const scheduled = missions.filter((m) => m.source === 'SCHEDULED' && m.tailNumber === tail);
      const sectors = scheduled
        .map((m) => {
          const key = String(m.bookingId || m.id || `${m.quoteId}:${m.legIndex}`);
          return {
            key,
            from: String(m.from || '').toUpperCase(),
            to: String(m.to || '').toUpperCase(),
            pax: Math.max(0, Number(m.paxCount || 0)),
            etd: String(m.etd || '').trim(),
            eta: String(m.eta || '').trim(),
            etdMin: parseClock(m.etd),
            bookingId: m.bookingId ? String(m.bookingId) : null,
            missionId: m.id ? String(m.id) : null,
            isSynthetic:
              key.startsWith('SPLAN-SOLD-') ||
              key.startsWith('SINV-SOLD-') ||
              String(m.quoteId || '').startsWith('SPLAN-') ||
              String(m.quoteId || '').startsWith('SINV-'),
          };
        })
        .filter((s) => !!s.from && !!s.to);

      const warnings: string[] = [];
      if (sectors.length === 0) {
        return {
          tail,
          sectorCount: 0,
          maxOnboard: 0,
          warnings: ['No assigned scheduled sectors'],
          summary: 'No route',
          stops: [] as Array<{
            sequence: number;
            location: string;
            arrivalTime: string;
            departureTime: string;
            boarding: number;
            dropping: number;
            onboardAfter: number;
            linkedSectorKeys: string[];
          }>,
        };
      }

      if (sectors.some((s) => !s.etd || !s.eta)) warnings.push('missing time');
      if (sectors.some((s) => s.isSynthetic)) warnings.push('synthetic sector');

      const outDegree: Record<string, number> = {};
      const inDegree: Record<string, number> = {};
      sectors.forEach((s) => {
        outDegree[s.from] = (outDegree[s.from] || 0) + 1;
        inDegree[s.to] = (inDegree[s.to] || 0) + 1;
      });

      const nodes = Array.from(new Set(sectors.flatMap((s) => [s.from, s.to])));
      const startCandidates = nodes.filter((n) => (outDegree[n] || 0) > (inDegree[n] || 0));
      const sortedByEtd = [...sectors].sort((a, b) => {
        const am = a.etdMin ?? Number.MAX_SAFE_INTEGER;
        const bm = b.etdMin ?? Number.MAX_SAFE_INTEGER;
        if (am !== bm) return am - bm;
        return a.key.localeCompare(b.key);
      });
      const startNode = startCandidates[0] || sortedByEtd[0]?.from || sectors[0].from;

      const unvisited = new Set(sectors.map((_, idx) => idx));
      const traversed: typeof sectors = [];
      let current = startNode;
      let disconnectedSegments = 0;

      while (unvisited.size > 0) {
        const choices = Array.from(unvisited)
          .map((idx) => ({ idx, edge: sectors[idx] }))
          .filter(({ edge }) => edge.from === current)
          .sort((a, b) => {
            const am = a.edge.etdMin ?? Number.MAX_SAFE_INTEGER;
            const bm = b.edge.etdMin ?? Number.MAX_SAFE_INTEGER;
            if (am !== bm) return am - bm;
            return a.edge.key.localeCompare(b.edge.key);
          });

        let picked = choices[0];
        if (!picked) {
          const remaining = Array.from(unvisited)
            .map((idx) => ({ idx, edge: sectors[idx] }))
            .sort((a, b) => {
              const am = a.edge.etdMin ?? Number.MAX_SAFE_INTEGER;
              const bm = b.edge.etdMin ?? Number.MAX_SAFE_INTEGER;
              if (am !== bm) return am - bm;
              return a.edge.key.localeCompare(b.edge.key);
            });
          picked = remaining[0];
          if (!picked) break;
          if (traversed.length > 0) disconnectedSegments += 1;
          current = picked.edge.from;
        }

        traversed.push(picked.edge);
        unvisited.delete(picked.idx);
        current = picked.edge.to;
      }

      if (disconnectedSegments > 0) warnings.push('disconnected route');

      const stopRows: Array<{
        location: string;
        arrivalTime: string;
        departureTime: string;
        boarding: number;
        dropping: number;
        linkedSectorKeys: string[];
      }> = [];

      const pushStopEvent = (
        location: string,
        patch: { arrivalTime?: string; departureTime?: string; boarding?: number; dropping?: number; key: string }
      ) => {
        const last = stopRows[stopRows.length - 1];
        if (last && last.location === location) {
          if (patch.arrivalTime && !last.arrivalTime) last.arrivalTime = patch.arrivalTime;
          if (patch.departureTime && !last.departureTime) last.departureTime = patch.departureTime;
          last.boarding += Number(patch.boarding || 0);
          last.dropping += Number(patch.dropping || 0);
          if (!last.linkedSectorKeys.includes(patch.key)) last.linkedSectorKeys.push(patch.key);
          return;
        }
        stopRows.push({
          location,
          arrivalTime: patch.arrivalTime || '',
          departureTime: patch.departureTime || '',
          boarding: Number(patch.boarding || 0),
          dropping: Number(patch.dropping || 0),
          linkedSectorKeys: [patch.key],
        });
      };

      traversed.forEach((s) => {
        pushStopEvent(s.from, { departureTime: s.etd, boarding: s.pax, key: s.key });
        pushStopEvent(s.to, { arrivalTime: s.eta, dropping: s.pax, key: s.key });
      });

      let onboard = 0;
      let maxOnboard = 0;
      let negativeAdjusted = false;
      const stops = stopRows.map((r, idx) => {
        onboard = onboard + r.boarding - r.dropping;
        if (onboard < 0) {
          negativeAdjusted = true;
          onboard = 0;
        }
        maxOnboard = Math.max(maxOnboard, onboard);
        return {
          sequence: idx + 1,
          location: r.location,
          arrivalTime: r.arrivalTime || '--:--',
          departureTime: r.departureTime || '--:--',
          boarding: r.boarding,
          dropping: r.dropping,
          onboardAfter: onboard,
          linkedSectorKeys: r.linkedSectorKeys,
        };
      });

      if (negativeAdjusted) warnings.push('negative load adjusted');

      const summary = stops
        .map((s) => s.location)
        .filter((loc, idx, arr) => idx === 0 || arr[idx - 1] !== loc)
        .join(' → ');

      return {
        tail,
        sectorCount: traversed.length,
        maxOnboard,
        warnings,
        summary,
        stops,
        sectors: traversed.map((s) => ({
          sectorKey: s.key,
          from: s.from,
          to: s.to,
          pax: s.pax,
          etd: s.etd || '',
          eta: s.eta || '',
          bookingId: s.bookingId || null,
          missionId: s.missionId || null,
          isSynthetic: s.isSynthetic,
        })),
      };
    });
  }, [tailEntries]);

  const [routeTailEdits, setRouteTailEdits] = React.useState<Record<string, RouteTailEditState>>({});

  const persistedRouteByTail = React.useMemo(() => {
    const map: Record<string, RoutePreviewCard> = {};
    Object.entries(plannerRoutesByTail || {}).forEach(([tailRaw, route]) => {
      const tail = String(tailRaw || '').trim().toUpperCase();
      if (!tail || !route) return;
      const stopsRaw = Array.isArray(route.stops) ? route.stops : [];
      const stops = stopsRaw.map((s, idx) => ({
        sequence: Number(s.sequence || idx + 1),
        location: String(s.locationCode || ''),
        arrivalTime: String(s.arrivalTimeLocal || '--:--'),
        departureTime: String(s.departureTimeLocal || '--:--'),
        boarding: Math.max(0, Number(s.boardingPax || 0)),
        dropping: Math.max(0, Number(s.droppingPax || 0)),
        onboardAfter: Math.max(0, Number(s.onboardAfterPax || 0)),
        linkedSectorKeys: (Array.isArray(route.sectorLinks) ? route.sectorLinks : [])
          .filter((l) => l.fromStopId === s.id || l.toStopId === s.id)
          .map((l) => String(l.sectorKey || ''))
          .filter(Boolean),
        notes: String(s.notes || ''),
        manual: Boolean(s.isManual),
      }));
      const warnings = Array.isArray(route.warnings)
        ? route.warnings.map((w: any) => String(w))
        : [];
      const summary =
        String(route.summaryPath || '').trim() ||
        stops.map((s) => s.location).filter((loc, idx, arr) => idx === 0 || arr[idx - 1] !== loc).join(' → ') ||
        'No route';
      map[tail] = {
        tail,
        sectorCount: Array.isArray(route.sectorLinks) ? route.sectorLinks.length : 0,
        maxOnboard: Math.max(0, Number(route.maxOnboardPax || 0)),
        warnings,
        summary,
        stops,
        sourceKind: 'persisted',
        persistedStatus: String(route.status || ''),
        routeId: String(route.id || ''),
        diagnostics: route.diagnostics,
        sectors: (Array.isArray(route.sectorLinks) ? route.sectorLinks : []).map((l: any) => ({
          sectorKey: String(l.sectorKey || ''),
          from: String(l?.metadata?.from || ''),
          to: String(l?.metadata?.to || ''),
          pax: Math.max(0, Number(l?.paxCount || 0)),
          etd: String(l?.etdLocal || ''),
          eta: String(l?.etaLocal || ''),
          bookingId: l?.bookingId ? String(l.bookingId) : null,
          missionId: l?.missionId ? String(l.missionId) : null,
          isSynthetic: Boolean(l?.isSyntheticSector),
        })),
      };
    });
    return map;
  }, [plannerRoutesByTail]);

  const recomputeRoutePreview = React.useCallback(
    (base: RoutePreviewCard, edit?: RouteTailEditState): RoutePreviewCard => {
      const seedStops = edit?.stops?.length
        ? edit.stops
        : base.stops.map((s) => ({ ...s, notes: s.notes || '', manual: false }));

      const stops: RoutePreviewStop[] = [];
      let onboard = 0;
      let maxOnboard = 0;
      let negativeAdjusted = false;
      seedStops.forEach((s, idx) => {
        const boarding = Math.max(0, Number(s.boarding || 0));
        const dropping = Math.max(0, Number(s.dropping || 0));
        onboard = onboard + boarding - dropping;
        if (onboard < 0) {
          negativeAdjusted = true;
          onboard = 0;
        }
        maxOnboard = Math.max(maxOnboard, onboard);
        stops.push({
          sequence: idx + 1,
          location: String(s.location || ''),
          arrivalTime: String(s.arrivalTime || '--:--'),
          departureTime: String(s.departureTime || '--:--'),
          boarding,
          dropping,
          onboardAfter: onboard,
          linkedSectorKeys: Array.isArray(s.linkedSectorKeys) ? s.linkedSectorKeys : [],
          notes: String(s.notes || ''),
          manual: Boolean(s.manual),
        });
      });

      const warningSet = new Set(base.warnings || []);
      if (stops.some((s) => !s.arrivalTime || !s.departureTime || s.arrivalTime === '--:--' || s.departureTime === '--:--')) {
        warningSet.add('missing time');
      }
      if (negativeAdjusted) warningSet.add('negative load adjusted');
      if (edit?.orderEdited) warningSet.add('edited stop order may not match sector links');

      const summary = stops
        .map((s) => s.location)
        .filter((loc, idx, arr) => idx === 0 || arr[idx - 1] !== loc)
        .join(' → ') || 'No route';

      return {
        ...base,
        maxOnboard,
        warnings: Array.from(warningSet),
        summary,
        stops,
      };
    },
    []
  );

  const routePreviewByTail = React.useMemo(
    () =>
      generatedRoutePreviewByTail.map((generated) => {
        const tail = String(generated.tail || '').trim().toUpperCase();
        const persisted = persistedRouteByTail[tail];
        const base = persisted || generated;
        const recomputed = recomputeRoutePreview(base, routeTailEdits[tail]);
        return {
          ...recomputed,
          sourceKind: persisted ? 'persisted' : 'preview',
          persistedStatus: persisted?.persistedStatus,
          routeId: persisted?.routeId,
          diagnostics: persisted?.diagnostics,
        };
      }),
    [generatedRoutePreviewByTail, persistedRouteByTail, recomputeRoutePreview, routeTailEdits]
  );

  const updateRouteStopField = React.useCallback(
    (tail: string, index: number, field: 'arrivalTime' | 'departureTime' | 'boarding' | 'dropping' | 'notes', value: string) => {
      setRouteTailEdits((prev) => {
        const base = routePreviewByTail.find((r) => r.tail === tail);
        if (!base) return prev;
        const existing = prev[tail];
        const seededStops = (existing?.stops?.length ? existing.stops : base.stops.map((s) => ({ ...s, notes: '', manual: false }))).map((s) => ({ ...s }));
        if (!seededStops[index]) return prev;
        if (field === 'boarding' || field === 'dropping') {
          const asNum = Number.parseInt(value, 10);
          seededStops[index][field] = Number.isFinite(asNum) ? Math.max(0, asNum) : 0;
        } else {
          seededStops[index][field] = value;
        }
        seededStops[index].manual = true;
        return {
          ...prev,
          [tail]: {
            ...existing,
            stops: seededStops,
          },
        };
      });
    },
    [routePreviewByTail]
  );

  const moveRouteStop = React.useCallback(
    (tail: string, index: number, direction: -1 | 1) => {
      setRouteTailEdits((prev) => {
        const base = routePreviewByTail.find((r) => r.tail === tail);
        if (!base) return prev;
        const existing = prev[tail];
        const seededStops = (existing?.stops?.length ? existing.stops : base.stops.map((s) => ({ ...s, notes: '', manual: false }))).map((s) => ({ ...s }));
        const target = index + direction;
        if (target < 0 || target >= seededStops.length) return prev;
        const a = seededStops[index];
        const b = seededStops[target];
        seededStops[index] = { ...b, manual: true };
        seededStops[target] = { ...a, manual: true };
        return {
          ...prev,
          [tail]: {
            ...existing,
            stops: seededStops,
            orderEdited: true,
          },
        };
      });
    },
    [routePreviewByTail]
  );

  const resetRoutePreview = React.useCallback((tail: string) => {
    setRouteTailEdits((prev) => {
      if (!prev[tail]) return prev;
      const next = { ...prev };
      delete next[tail];
      return next;
    });
  }, []);

  const [routeManualSavingTail, setRouteManualSavingTail] = React.useState<string | null>(null);
  const [routeActionTail, setRouteActionTail] = React.useState<string | null>(null);

  const escapeHtml = (value: any): string =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const buildRouteSheetHtml = React.useCallback(
    (route: RoutePreviewCard, options: { unsavedManualEdits: boolean; isPersisted: boolean; persistedStatus: string }) => {
      const generatedAt = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
      const warningItems = (route.warnings || []).map((w) => `<li>${escapeHtml(w)}</li>`).join('');
      const stopRows = (route.stops || [])
        .map(
          (s) => `
            <tr>
              <td>${s.sequence}</td>
              <td>${escapeHtml(s.location)}</td>
              <td>${escapeHtml(s.arrivalTime || '--:--')}</td>
              <td>${escapeHtml(s.departureTime || '--:--')}</td>
              <td>${s.boarding}</td>
              <td>${s.dropping}</td>
              <td>${s.onboardAfter}</td>
              <td>${escapeHtml(s.notes || '')}</td>
            </tr>
          `
        )
        .join('');
      const sectorsRows = (route.sectors || [])
        .map(
          (s) => `
            <tr>
              <td>${escapeHtml(s.sectorKey)}</td>
              <td>${escapeHtml(s.from)} &rarr; ${escapeHtml(s.to)}</td>
              <td>${s.pax}</td>
              <td>${escapeHtml(s.etd || '--:--')} / ${escapeHtml(s.eta || '--:--')}</td>
              <td>${escapeHtml(s.bookingId || '')}</td>
              <td>${escapeHtml(s.missionId || '')}</td>
              <td>${s.isSynthetic ? '<span class="badge">SYNTHETIC</span>' : ''}</td>
            </tr>
          `
        )
        .join('');

      return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>MAOS Route Sheet ${escapeHtml(route.tail)} ${escapeHtml(selectedDate)}</title>
  <style>
    @page { size: A4; margin: 12mm; }
    body { font-family: Arial, sans-serif; background: #fff; color: #111; margin: 0; }
    .sheet { padding: 8px 10px; }
    .header { border-bottom: 2px solid #0f172a; padding-bottom: 8px; margin-bottom: 10px; }
    .title { font-size: 18px; font-weight: 800; letter-spacing: 0.04em; }
    .sub { font-size: 11px; margin-top: 4px; color: #334155; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 16px; font-size: 11px; margin: 10px 0; }
    .warn { margin: 8px 0; padding: 6px 8px; background: #fff7ed; border: 1px solid #fdba74; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 10px; }
    th, td { border: 1px solid #d1d5db; padding: 4px 5px; text-align: left; vertical-align: top; }
    th { background: #f8fafc; font-weight: 700; }
    .section { margin-top: 10px; }
    .section h3 { margin: 0 0 4px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; }
    .badge { border: 1px solid #f59e0b; background: #fffbeb; color: #92400e; border-radius: 999px; padding: 1px 6px; font-size: 9px; font-weight: 700; }
    .footer { margin-top: 14px; font-size: 10px; color: #475569; border-top: 1px solid #e2e8f0; padding-top: 6px; }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="header">
      <div class="title">Miracle Air Operating System - Route Sheet</div>
      <div class="sub">Generated at ${escapeHtml(generatedAt)}</div>
    </div>
    <div class="meta">
      <div><strong>Date:</strong> ${escapeHtml(selectedDate)}</div>
      <div><strong>Tail:</strong> ${escapeHtml(route.tail)}</div>
      <div><strong>Status:</strong> ${escapeHtml(options.persistedStatus || (options.isPersisted ? 'PERSISTED' : 'PREVIEW'))}</div>
      <div><strong>Max Onboard:</strong> ${route.maxOnboard}</div>
      <div><strong>Route:</strong> ${escapeHtml(route.summary || 'No route')}</div>
      <div><strong>Route Source:</strong> ${options.isPersisted ? 'Persisted route' : 'Preview route - not persisted'}</div>
    </div>
    ${options.unsavedManualEdits ? '<div class="warn"><strong>Warning:</strong> Unsaved manual edits included in this print/export.</div>' : ''}
    ${warningItems ? `<div class="warn"><strong>Route warnings:</strong><ul>${warningItems}</ul></div>` : ''}
    <div class="section">
      <h3>Stops</h3>
      <table>
        <thead>
          <tr>
            <th>#</th><th>Location</th><th>Arrival</th><th>Departure</th><th>+Board</th><th>-Drop</th><th>Onboard</th><th>Notes</th>
          </tr>
        </thead>
        <tbody>
          ${stopRows || '<tr><td colspan="8">No stops</td></tr>'}
        </tbody>
      </table>
    </div>
    <div class="section">
      <h3>Linked Sectors</h3>
      <table>
        <thead>
          <tr>
            <th>Sector Key</th><th>Route</th><th>Pax</th><th>ETD / ETA</th><th>Booking ID</th><th>Mission ID</th><th>Flags</th>
          </tr>
        </thead>
        <tbody>
          ${sectorsRows || '<tr><td colspan="7">No linked sectors</td></tr>'}
        </tbody>
      </table>
    </div>
    <div class="footer">MAOS Planner Route Sheet</div>
  </div>
</body>
</html>`;
    },
    [selectedDate]
  );

  const handlePrintRouteSheet = React.useCallback((route: RoutePreviewCard, opts: { unsavedManualEdits: boolean; isPersisted: boolean; persistedStatus: string }) => {
    const html = buildRouteSheetHtml(route, opts);
    const win = window.open('', '_blank', 'noopener,noreferrer,width=980,height=820');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    window.setTimeout(() => win.print(), 150);
  }, [buildRouteSheetHtml]);

  const handleDownloadRouteSheetHtml = React.useCallback(async (route: RoutePreviewCard, opts: { unsavedManualEdits: boolean; isPersisted: boolean; persistedStatus: string }) => {
    let html = '';
    if (opts.isPersisted && route.routeId) {
      try {
        html = await plannerService.getPlannerRouteSheetHtml(String(route.routeId));
      } catch {
        html = '';
      }
    }
    if (!html) {
      html = buildRouteSheetHtml(route, opts);
    }
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `maos-route-sheet-${String(route.tail || 'tail').toLowerCase()}-${selectedDate}.html`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [buildRouteSheetHtml, selectedDate]);

  const handleSaveManualEditsForTail = React.useCallback(
    async (route: RoutePreviewCard) => {
      const routeId = String(route.routeId || '').trim();
      if (!routeId || !onSaveManualRouteEdits) return;
      setRouteManualSavingTail(route.tail);
      try {
        const payloadStops: PlannerRouteStopUpdateInput[] = route.stops.map((s) => ({
          sequence: s.sequence,
          locationCode: String(s.location || '').toUpperCase(),
          arrivalTimeLocal: s.arrivalTime && s.arrivalTime !== '--:--' ? s.arrivalTime : undefined,
          departureTimeLocal: s.departureTime && s.departureTime !== '--:--' ? s.departureTime : undefined,
          boardingPax: Math.max(0, Number(s.boarding || 0)),
          droppingPax: Math.max(0, Number(s.dropping || 0)),
          onboardAfterPax: Math.max(0, Number(s.onboardAfter || 0)),
          notes: String(s.notes || ''),
          isManual: true,
        }));
        const ok = await onSaveManualRouteEdits(routeId, {
          stops: payloadStops,
          warnings: route.warnings,
          diagnostics: route.diagnostics,
          summaryPath: route.summary,
          maxOnboardPax: route.maxOnboard,
        });
        if (ok) resetRoutePreview(route.tail);
      } finally {
        setRouteManualSavingTail(null);
      }
    },
    [onSaveManualRouteEdits, resetRoutePreview]
  );

  const handleRouteAction = React.useCallback(
    async (tail: string, routeId: string, fn?: (routeId: string) => Promise<boolean>) => {
      if (!routeId || !fn) return;
      setRouteActionTail(tail);
      try {
        await fn(routeId);
      } finally {
        setRouteActionTail(null);
      }
    },
    []
  );

  const fleetStats = React.useMemo(() => {
    const active = tailEntries.filter((e) => e.missions.length > 0).length;
    const idle = Math.max(0, tailEntries.length - active);
    return {
      active,
      idle,
      total: tailEntries.length,
      queue: queueMissions.length,
      assigned: tailEntries.reduce((sum, e) => sum + e.missions.length, 0),
    };
  }, [tailEntries, queueMissions.length]);

  const crewStats = React.useMemo(() => {
    const values = Object.values(crewDutyMap || {}) as Array<{ status: 'ON_DUTY' | 'STANDBY' | 'OFF'; duties: any[] }>;
    const onDuty = values.filter(v => v.status === 'ON_DUTY').length;
    const standby = values.filter(v => v.status === 'STANDBY').length;
    const assigned = tailEntries.reduce((sum, e) => {
      const crew = new Set(e.missions.flatMap((m) => m.assignedCrew || []));
      return sum + crew.size;
    }, 0);
    return { onDuty, standby, assigned };
  }, [crewDutyMap, tailEntries]);

  const renderCrewMissionCard = (m: TacticalMission) => (
    <div
      key={`crew-${m.quoteId}-${m.id}`}
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.stopPropagation(); onDropPilotOnLeg(m.quoteId, m.id); }}
      className={`bg-white rounded-xl p-4 border border-slate-200 shadow-sm transition-all relative ${
        draggedPilotId ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-transparent' : 'hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.12em] border ${
              m.source === 'SCHEDULED' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-indigo-200 bg-indigo-50 text-indigo-700'
            }`}>
              {m.source}
            </span>
            <span className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">
              Pax {m.paxCount ?? 0}
            </span>
          </div>
          <p className="mt-2 text-sm font-black text-slate-900 uppercase">
            {m.from} → {m.to}
          </p>
          <p className="text-[11px] font-semibold text-slate-500">
            {m.etd || '--:--'} - {m.eta || '--:--'} • {m.bookingId || m.quoteId.slice(0, 8)}
          </p>
        </div>
        <span className="text-[10px] font-black text-indigo-600 uppercase">
          {(m.assignedCrew || []).length} Crew
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="flex -space-x-3">
          {m.assignedCrew?.map(id => {
            const p = pilots.find(u => u.id === id);
            const photoUrl = (p as any)?.photoUrl || (p as any)?.avatar || '';
            const displayName = (p as any)?.name || `${(p as any)?.firstName || ''} ${(p as any)?.lastName || ''}`.trim() || (p as any)?.email || 'Crew';
            const initial = displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : '?';
            return (
              <div key={id} className="w-9 h-9 rounded-full border-4 border-white bg-slate-900 text-white text-[9px] font-black flex items-center justify-center shadow-lg relative group/pilot overflow-hidden" title={displayName}>
                {photoUrl ? <img src={photoUrl} className="w-full h-full object-cover" alt={displayName} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
                {!photoUrl && initial}
                {isDFO && (
                  <button
                    onClick={() => onUnassignPilot(id, m.quoteId, m.id)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/pilot:opacity-100 transition-all"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                )}
              </div>
            );
          })}
          {(!m.assignedCrew || m.assignedCrew.length === 0) && (
            <div className="w-9 h-9 rounded-full border-4 border-white bg-slate-100 flex items-center justify-center text-slate-300">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4"/></svg>
            </div>
          )}
        </div>
      </div>

      {renderInboundHubBadges(m, true)}

      {!!m.assignedCrew?.length && (
        <div className="mt-2 space-y-1">
          {m.assignedCrew.map(id => {
            const crew = pilots.find(u => u.id === id);
            const displayName = (crew as any)?.name || `${(crew as any)?.firstName || ''} ${(crew as any)?.lastName || ''}`.trim() || (crew as any)?.email || 'Crew';
            const roleCode = getCrewRoleCode(id);
            const roleLabel =
              roleCode === 'CP'
                ? 'Chief Pilot'
                : roleCode === 'DFO'
                ? 'DFO / Pilot'
                : roleCode === 'FIRST_OFFICER'
                ? 'First Officer'
                : roleCode === 'PILOT'
                ? 'Pilot'
                : roleCode;
            return (
              <div key={`${m.quoteId}-${m.id}-${id}`} className="text-[10px] font-semibold text-slate-600 flex items-center justify-between">
                <span className="truncate pr-2">{displayName}</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-400">{roleLabel || 'Crew'}</span>
              </div>
            );
          })}
        </div>
      )}

      {crewLegErrors[`${m.quoteId}:${m.id}`] && (
        <p className="mt-2 text-[10px] font-semibold text-rose-600">
          {crewLegErrors[`${m.quoteId}:${m.id}`]}
        </p>
      )}
      {draggedPilotId && (
        <div className="absolute inset-0 bg-indigo-600/5 rounded-2xl border-2 border-indigo-400 border-dashed animate-pulse flex items-center justify-center z-10">
          <span className="text-[9px] font-black text-indigo-600 uppercase">Drop Pilot Here</span>
        </div>
      )}
    </div>
  );

  return (
    <>
      {viewMode === 'ROUTE' ? (
        <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
          <div className="mb-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-3">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-indigo-700">Route Builder</p>
            <p className="mt-1 text-sm font-black text-indigo-900">Route Builder — generated routes can be saved; manual edits require Save manual edits.</p>
          </div>
          <div className="grid gap-3 xl:grid-cols-2">
            {routePreviewByTail.map((route) => {
              const edited = Boolean(routeTailEdits[route.tail]);
              const persistedStatus = String(route.persistedStatus || '').toUpperCase();
              const isPersisted = route.sourceKind === 'persisted';
              const saveBlockedByManual = edited;
              const saveBlockedByStatus = persistedStatus === 'LOCKED' || persistedStatus === 'CONFIRMED';
              const isLocked = persistedStatus === 'LOCKED';
              const isConfirmed = persistedStatus === 'CONFIRMED';
              const editDisabled = saveBlockedByStatus;
              const saveDisabled = saveBlockedByManual || saveBlockedByStatus || !onSaveGeneratedRouteForTail || routeSavingTail === route.tail;
              const canSaveManual = isPersisted && edited && !!route.routeId && !saveBlockedByStatus && !!onSaveManualRouteEdits;
              const manualSaveDisabled = !canSaveManual || routeManualSavingTail === route.tail;
              const canLock = isPersisted && !!route.routeId && (persistedStatus === 'DRAFT' || persistedStatus === 'GENERATED' || persistedStatus === 'MANUAL_EDITED') && !routeActionTail;
              const canConfirm = isPersisted && !!route.routeId && persistedStatus === 'LOCKED' && !routeActionTail;
              const canReset = isPersisted && !!route.routeId && !isConfirmed && !routeActionTail;
              return (
              <div key={`route-${route.tail}`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-black font-mono uppercase text-slate-900">{route.tail}</p>
                    <p className="mt-1 text-[11px] font-semibold text-slate-600">{route.summary}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] ${
                        isPersisted ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-700'
                      }`}>
                        {isPersisted ? 'Persisted' : 'Preview only'}
                      </span>
                      {persistedStatus === 'MANUAL_EDITED' && (
                        <span className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-sky-700">
                          Manual edited
                        </span>
                      )}
                      {(persistedStatus === 'LOCKED' || persistedStatus === 'CONFIRMED') && (
                        <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-amber-700">
                          {persistedStatus}
                        </span>
                      )}
                    </div>
                    {edited && (
                      <span className="mt-1 inline-flex rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-sky-700">
                        Edited preview
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">{route.sectorCount} sectors</p>
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-600">Max onboard {route.maxOnboard}</p>
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-rose-600">{route.warnings.length} warnings</p>
                    {edited && (
                      <button
                        type="button"
                        onClick={() => resetRoutePreview(route.tail)}
                        className="mt-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-100"
                      >
                        Reset preview
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={saveDisabled}
                      title={
                        saveBlockedByManual
                          ? 'Manual edits are local preview only and are not persisted yet.'
                          : saveBlockedByStatus
                          ? `Route is ${persistedStatus}; regenerate disabled.`
                          : 'Save generated route to planner persistence.'
                      }
                      onClick={() => onSaveGeneratedRouteForTail?.(route.tail)}
                      className={`mt-1 block rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${
                        saveDisabled
                          ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                      }`}
                    >
                      {routeSavingTail === route.tail ? 'Saving...' : 'Save generated route'}
                    </button>
                    <button
                      type="button"
                      disabled={manualSaveDisabled}
                      title={
                        !isPersisted
                          ? 'Save generated route first.'
                          : !edited
                          ? 'No local manual edits to save.'
                          : saveBlockedByStatus
                          ? `Route is ${persistedStatus}; manual save disabled.`
                          : 'Save local manual route edits to planner.'
                      }
                      onClick={() => handleSaveManualEditsForTail(route)}
                      className={`mt-1 block rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${
                        manualSaveDisabled
                          ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                      }`}
                    >
                      {routeManualSavingTail === route.tail ? 'Saving...' : 'Save manual edits'}
                    </button>
                    {!isPersisted && edited && (
                      <p className="mt-1 text-[10px] font-semibold text-amber-700">Save generated route first.</p>
                    )}
                    <div className="mt-1 flex flex-wrap justify-end gap-1">
                      <button
                        type="button"
                        disabled={!canLock}
                        onClick={() => handleRouteAction(route.tail, String(route.routeId || ''), onLockRoute)}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${canLock ? 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100' : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                      >
                        {routeActionTail === route.tail && canLock ? '...' : 'Lock'}
                      </button>
                      <button
                        type="button"
                        disabled={!canConfirm}
                        onClick={() => handleRouteAction(route.tail, String(route.routeId || ''), onConfirmRoute)}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${canConfirm ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                      >
                        {routeActionTail === route.tail && canConfirm ? '...' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        disabled={!canReset}
                        onClick={() => handleRouteAction(route.tail, String(route.routeId || ''), onResetRoute)}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${canReset ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                      >
                        {routeActionTail === route.tail && canReset ? '...' : 'Reset persisted route'}
                      </button>
                    </div>
                    <div className="mt-1 flex flex-wrap justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handlePrintRouteSheet(route, {
                          unsavedManualEdits: edited,
                          isPersisted,
                          persistedStatus: persistedStatus || (isPersisted ? 'PERSISTED' : 'PREVIEW'),
                        })}
                        className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                      >
                        Print Route Sheet
                      </button>
                      <button
                        type="button"
                        onClick={() => { void handleDownloadRouteSheetHtml(route, {
                          unsavedManualEdits: edited,
                          isPersisted,
                          persistedStatus: persistedStatus || (isPersisted ? 'PERSISTED' : 'PREVIEW'),
                        }); }}
                        className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                      >
                        Download Route Sheet HTML
                      </button>
                    </div>
                  </div>
                </div>

                {route.warnings.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {route.warnings.map((w) => (
                      <span key={`${route.tail}-${w}`} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-amber-700">
                        {w}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-left">
                    <thead>
                      <tr className="text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
                        <th className="py-1 pr-2">#</th>
                        <th className="py-1 pr-2">Stop</th>
                        <th className="py-1 pr-2">Arr</th>
                        <th className="py-1 pr-2">Dep</th>
                        <th className="py-1 pr-2">+Board</th>
                        <th className="py-1 pr-2">-Drop</th>
                        <th className="py-1 pr-2">Onboard</th>
                        <th className="py-1 pr-2">Linked Sectors</th>
                        <th className="py-1 pr-2">Notes</th>
                        <th className="py-1 pr-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {route.stops.map((s, idx) => (
                        <tr key={`${route.tail}-${s.sequence}-${s.location}`} className="border-t border-slate-100 text-[11px] font-semibold text-slate-700">
                          <td className="py-1 pr-2">{s.sequence}</td>
                          <td className="py-1 pr-2 font-black text-slate-900">
                            <div className="flex items-center gap-1">
                              <span>{s.location}</span>
                              {s.manual && (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-[0.1em] text-amber-700">
                                  Manual
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={s.arrivalTime}
                              onChange={(e) => updateRouteStopField(route.tail, idx, 'arrivalTime', e.target.value)}
                              disabled={editDisabled}
                              className="w-16 rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px] font-semibold text-slate-700"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={s.departureTime}
                              onChange={(e) => updateRouteStopField(route.tail, idx, 'departureTime', e.target.value)}
                              disabled={editDisabled}
                              className="w-16 rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px] font-semibold text-slate-700"
                            />
                          </td>
                          <td className="py-1 pr-2 text-emerald-700">
                            <input
                              type="number"
                              min={0}
                              value={s.boarding}
                              onChange={(e) => updateRouteStopField(route.tail, idx, 'boarding', e.target.value)}
                              disabled={editDisabled}
                              className="w-14 rounded border border-emerald-200 bg-emerald-50 px-1 py-0.5 text-[10px] font-black text-emerald-700"
                            />
                          </td>
                          <td className="py-1 pr-2 text-rose-700">
                            <input
                              type="number"
                              min={0}
                              value={s.dropping}
                              onChange={(e) => updateRouteStopField(route.tail, idx, 'dropping', e.target.value)}
                              disabled={editDisabled}
                              className="w-14 rounded border border-rose-200 bg-rose-50 px-1 py-0.5 text-[10px] font-black text-rose-700"
                            />
                          </td>
                          <td className="py-1 pr-2 font-black text-indigo-700">{s.onboardAfter}</td>
                          <td className="py-1 pr-2 text-[10px] text-slate-500">{s.linkedSectorKeys.join(', ')}</td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={s.notes || ''}
                              onChange={(e) => updateRouteStopField(route.tail, idx, 'notes', e.target.value)}
                              disabled={editDisabled}
                              placeholder="Stop notes"
                              className="w-28 rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px] font-semibold text-slate-700"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => moveRouteStop(route.tail, idx, -1)}
                                disabled={idx === 0 || editDisabled}
                                className={`rounded border px-1 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${idx === 0 || editDisabled ? 'border-slate-200 bg-slate-100 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'}`}
                              >
                                Up
                              </button>
                              <button
                                type="button"
                                onClick={() => moveRouteStop(route.tail, idx, 1)}
                                disabled={idx === route.stops.length - 1 || editDisabled}
                                className={`rounded border px-1 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${idx === route.stops.length - 1 || editDisabled ? 'border-slate-200 bg-slate-100 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'}`}
                              >
                                Down
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {route.stops.length === 0 && (
                        <tr>
                          <td colSpan={10} className="py-3 text-[11px] font-semibold text-slate-500">
                            No route stops to preview.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )})}
          </div>
        </div>
      ) : (
      <div id="planner-workspace" className="flex-1 flex gap-5 overflow-hidden scroll-mt-4">
        <div className={`${viewMode === 'CREW' ? 'w-[360px]' : 'w-[360px]'} shrink-0 flex flex-col space-y-4 overflow-hidden`}>
          {viewMode === 'CREW' ? (
            <div className="bg-slate-900 rounded-3xl p-6 flex flex-col gap-5 shadow-xl overflow-hidden flex-1 border border-slate-700/70">
              <div className="flex justify-between items-center">
                <h3 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.3em]">Command Pilot Bench</h3>
                <span className="text-[8px] font-bold text-slate-500 uppercase">Drag to Leg</span>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-3" onDragOver={e => e.preventDefault()}>
                {crewLoading && (
                  <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-[9px] font-black uppercase tracking-widest text-slate-400 text-center">
                    Loading duty roster...
                  </div>
                )}
                {!crewLoading && dutyFilteredCrew.length === 0 && (
                  <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-[9px] font-black uppercase tracking-widest text-slate-400 text-center">
                    No on-duty or standby pilots for this date.
                  </div>
                )}
                {dutyFilteredCrew.map(p => {
                  const dutyPercent = ((p.dutyHoursToday || 0) / (p.maxDutyHours || 12)) * 100;
                  const dutyStatus = crewDutyMap[p.id]?.status || 'OFF';
                  const photoUrl = (p as any).photoUrl || (p as any).avatar || '';
                  const displayName = (p as any).name || `${(p as any).firstName || ''} ${(p as any).lastName || ''}`.trim() || (p as any).email || 'Unknown';
                  const initial = displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : '?';
                  return (
                    <div key={p.id} draggable={isDFO} onDragStart={() => onDragPilotStart(p.id)} className="bg-white/5 border border-white/10 p-4 rounded-2xl flex items-center gap-4 group cursor-grab hover:bg-white/10 transition-all">
                      <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center text-white font-black text-xs uppercase shadow-lg overflow-hidden">
                        {photoUrl ? (
                          <img src={photoUrl} className="w-full h-full object-cover" alt={displayName} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : null}
                        {!photoUrl && initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-black text-white uppercase truncate">{displayName}</p>
                        <div className="w-full h-1 bg-white/5 rounded-full mt-2 overflow-hidden">
                          <div className={`h-full ${dutyPercent > 80 ? 'bg-rose-500' : 'bg-indigo-500'}`} style={{ width: `${dutyPercent}%` }}></div>
                        </div>
                      </div>
                      {dutyStatus === 'STANDBY' && (
                        <span className="px-2 py-1 rounded-full bg-amber-400/20 text-amber-300 text-[8px] font-black uppercase tracking-widest border border-amber-300/20">
                          Standby
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={onDropOnReservoir}
              className={`flex-1 flex flex-col space-y-5 overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 transition-all p-4 ${draggedMissionId ? 'bg-indigo-50 border-2 border-dashed border-indigo-300' : ''}`}
            >
            {!queueCollapsed ? (
            <>
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-[0.18em]">Mission Queue</h3>
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600">{queueMissions.length} open</span>
                  <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700">{selectedQueueKeys.length} selected</span>
                  <button
                    type="button"
                    onClick={() => setQueueCollapsed(true)}
                    className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600 hover:bg-slate-100"
                  >
                    Minimize
                  </button>
                </div>
              </div>
                <div className="mt-2 flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1">
                  {(['ALL', 'CHARTER', 'SCHEDULED'] as const).map(tab => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setPoolTab(tab)}
                      className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] transition ${
                        poolTab === tab
                          ? tab === 'SCHEDULED'
                            ? 'bg-emerald-600 text-white'
                            : tab === 'CHARTER'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-800 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={queueQuery}
                  onChange={(e) => setQueueQuery(e.target.value)}
                  placeholder="Search client, route, quote, booking"
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                />
                <p className="mt-2 text-[10px] font-semibold text-slate-500">Drag any mission card and drop it on an aircraft tail.</p>
                <div className="mt-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleSelectAllVisible}
                    disabled={visibleQueueKeys.length === 0}
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                      visibleQueueKeys.length === 0
                        ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    disabled={selectedQueueKeys.length === 0}
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                      selectedQueueKeys.length === 0
                        ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    Clear
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between rounded-xl border border-indigo-200 bg-indigo-50 p-2">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700">
                    Selected {selectedQueueKeys.length} • Target {selectedTail || 'None'}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={handleBatchAssignToBestTail}
                      disabled={selectedQueueKeys.length === 0}
                      className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] ${
                        selectedQueueKeys.length === 0
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-emerald-600 text-white hover:bg-emerald-500'
                      }`}
                    >
                      Auto Best Tail
                    </button>
                    <button
                      type="button"
                      onClick={handleBatchAssignToSelectedTail}
                      disabled={!selectedTail || selectedQueueKeys.length === 0}
                      className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] ${
                        !selectedTail || selectedQueueKeys.length === 0
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-indigo-600 text-white hover:bg-indigo-500'
                      }`}
                    >
                      Assign To Selected Tail
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-2 pb-2">
                {queueMissions.map(m => {
                  const isScheduled = m.source === 'SCHEDULED';
                  return (
                    <div
                      key={`${m.source}-${m.quoteId}-${m.id}`}
                      draggable={isFlightPlanner}
                      onDragStart={() => onDragMissionStart(m.source, m.quoteId, resolveMissionAssignmentId(m))}
                      className={`bg-white p-3 rounded-xl border shadow-sm transition-all cursor-grab hover:shadow-md group ${
                        isScheduled ? 'border-emerald-200' : 'border-indigo-200'
                      }`}
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedQueueKeys.includes(`${m.source}|${m.quoteId}|${m.id}`)}
                            onChange={() => toggleQueueSelection(m)}
                            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] ${
                          isScheduled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                          }`}>
                            {isScheduled ? 'Scheduled' : m.productMode}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => onOpenPaxModal(m)}
                            className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] text-slate-600 hover:bg-slate-100"
                            title="View passenger list"
                          >
                            Pax {m.paxCount ?? 0}
                          </button>
                          <PlanningWeatherNode code={m.to} />
                        </div>
                      </div>
                      <p className="truncate text-sm font-black text-slate-900">{m.customerName}</p>
                      <div className="mt-1 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.08em] text-slate-600">
                        <span>{m.from}</span>
                        <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                        <span>{m.to}</span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[10px] font-semibold text-slate-500">
                        <span>{m.etd && m.eta ? `${m.etd} - ${m.eta}` : 'Time pending'}</span>
                        <span className="font-black text-slate-400">{m.bookingId || m.quoteId.slice(0, 8)}</span>
                      </div>
                      {renderInboundHubBadges(m)}
                    </div>
                  );
                })}
                {queueMissions.length === 0 && (
                  <div className="p-10 text-center text-slate-400 uppercase font-black text-[10px] tracking-[0.14em] border-2 border-dashed border-slate-300 rounded-2xl bg-white">
                    No missions in selected queue
                  </div>
                )}
              </div>
            </>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-700">Queue Dock</p>
                  <button
                    type="button"
                    onClick={() => setQueueCollapsed(false)}
                    className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700 hover:bg-indigo-100"
                  >
                    Open Queue
                  </button>
                </div>
                <p className="mt-1 text-[11px] font-semibold text-slate-500">
                  {queueMissions.length} open mission(s) • {selectedQueueKeys.length} selected
                </p>
              </div>
            )}
        </div>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-3 overflow-hidden">
          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-black uppercase tracking-[0.14em] text-slate-800">Fleet Schedule</p>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-emerald-700">Active {fleetStats.active}</span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-slate-700">Idle {fleetStats.idle}</span>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-amber-700">Queue {fleetStats.queue}</span>
              </div>
              <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1">
                {(['FOCUS', 'GRID'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setTailViewMode(mode)}
                    className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${tailViewMode === mode ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    {mode === 'FOCUS' ? 'Single Tail' : 'All Tails'}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {orderedTails.map((tail) => {
                const legCount = fleetSchedule[tail]?.length || 0;
                const active = selectedTail === tail;
                return (
                  <button
                    key={tail}
                    type="button"
                    onClick={() => jumpToTail(tail)}
                    className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] transition ${
                      active
                        ? 'border-indigo-300 bg-indigo-600 text-white'
                        : legCount > 0
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {tail} {legCount > 0 ? <span className="ml-1">●</span> : ''}
                  </button>
                );
              })}
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Sort Legs</span>
                {(['ETD', 'CLIENT', 'ROUTE'] as const).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTailSortBy(key)}
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${tailSortBy === key ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                  >
                    {key}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setTailSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'))}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-100"
                >
                  {tailSortDir === 'ASC' ? 'Asc' : 'Desc'}
                </button>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setQueueCollapsed(v => !v)} className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700 hover:bg-indigo-100">{queueCollapsed ? 'Open Queue' : 'Collapse Queue'}</button>
                <button type="button" onClick={() => setTailViewMode('GRID')} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-100">View All</button>
              </div>
            </div>
          </div>

          <div ref={aircraftRailRef} className="flex-1 overflow-y-auto custom-scrollbar pr-2 pb-2">
          {tailViewMode === 'FOCUS' && selectedTailEntry ? (
            (() => {
              const tail = selectedTailEntry.tail;
              const group = sortMissions(collapseScheduledOpsLegs(selectedTailEntry.missions));
              const ac = aircrafts.find(a => a.tailNumber === tail);
              if (!ac) return null;
              const fuelTotal = (ac.fuelStatus?.leftWing || 0) + (ac.fuelStatus?.rightWing || 0);
              return (
                <div
                  key={`focus-${tail}`}
                  ref={(el) => {
                    tailColumnRefs.current[tail] = el;
                  }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => onDropOnAircraft(tail)}
                  className={`h-full rounded-3xl border overflow-hidden flex flex-col bg-white shadow-lg min-h-[640px] ${
                    selectedTail === tail ? 'border-indigo-300' : 'border-slate-200'
                  }`}
                >
                  <div className={`sticky top-0 z-10 border-b p-4 ${viewMode === 'GROUND' ? 'border-emerald-700 bg-emerald-950/95 text-white' : 'border-slate-200 bg-white/95 text-slate-900'} backdrop-blur`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-4xl font-black tracking-tight font-mono uppercase">{tail}</h3>
                        <p className="text-xs font-semibold text-slate-500">{ac.model || ac.registrationType || 'Fleet aircraft'}</p>
                      </div>
                      <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                        viewMode === 'GROUND'
                          ? 'border-white/20 bg-white/10 text-white'
                          : 'border-indigo-200 bg-indigo-50 text-indigo-700'
                      }`}>
                        {group.length} leg{group.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      {viewMode === 'GROUND' ? (
                        <div className="flex-1">
                          <div className="mb-1 flex items-center justify-between text-[10px] font-black uppercase tracking-[0.12em] text-emerald-200">
                            <span>Onboard Fuel</span>
                            <span>{fuelTotal}L</span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                            <div className="h-full bg-emerald-500" style={{ width: `${Math.min((fuelTotal / 1500) * 100, 100)}%` }} />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Crew</span>
                          <div className="flex -space-x-2">
                            {Array.from(new Set(group.flatMap(m => m.assignedCrew || []))).slice(0, 6).map(id => {
                              const p = pilots.find(u => u.id === id);
                              const photoUrl = (p as any)?.photoUrl || (p as any)?.avatar || '';
                              const displayName = (p as any)?.name || `${(p as any)?.firstName || ''} ${(p as any)?.lastName || ''}`.trim() || (p as any)?.email || 'Crew';
                              const initial = displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : '?';
                              return (
                                <div key={id} className="h-8 w-8 overflow-hidden rounded-full border-2 border-white bg-indigo-600 text-[10px] font-black text-white flex items-center justify-center" title={displayName}>
                                  {photoUrl ? <img src={photoUrl} className="h-full w-full object-cover" alt={displayName} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : initial}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`flex-1 space-y-3 overflow-y-auto custom-scrollbar p-4 ${viewMode === 'GROUND' ? 'bg-emerald-50/30' : 'bg-slate-50/60'}`}>
                    {group.length === 0 ? (
                      <div className="h-full min-h-[360px] rounded-xl border-2 border-dashed border-slate-200 bg-white px-4 py-10 text-center flex flex-col items-center justify-center">
                        <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Tail Idle</p>
                        <p className="mt-1 text-xs font-semibold text-slate-500">Drag missions from queue to assign</p>
                      </div>
                    ) : (
                      group.map(m => viewMode === 'CREW' ? renderCrewMissionCard(m) : (
                        <div
                          key={`${m.quoteId}-${m.id}`}
                          draggable={isFlightPlanner}
                          onDragStart={() => onDragMissionStart(m.source, m.quoteId, resolveMissionAssignmentId(m))}
                          onDragOver={e => e.preventDefault()}
                          onDrop={e => { e.stopPropagation(); onDropPilotOnLeg(m.quoteId, m.id); }}
                          className={`bg-white rounded-xl p-4 border border-slate-200 shadow-sm transition-all group/card relative flex flex-col ${draggedPilotId ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-transparent' : 'hover:shadow-md cursor-grab active:cursor-grabbing'}`}
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.12em] border ${m.source === 'SCHEDULED' ? 'border-emerald-100 bg-emerald-50 text-emerald-600' : 'border-indigo-100 bg-indigo-50 text-indigo-600'}`}>
                              {m.source === 'SCHEDULED' ? 'SCHEDULED' : m.productMode}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => onOpenPaxModal(m)}
                                className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.1em] border bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                                title="View passenger list"
                              >
                                Pax {m.paxCount ?? 0}
                              </button>
                              <PlanningWeatherNode code={m.to} />
                            </div>
                          </div>
                          <h5 className="mb-2 line-clamp-1 text-base font-black text-slate-800">{m.customerName}</h5>
                          <div className="mb-3 flex items-center gap-3 text-[11px] font-black uppercase text-slate-500">
                            <span>{m.from}</span>
                            <svg className="w-4 h-4 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                            <span>{m.to}</span>
                          </div>
                          <div className="mb-2 flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-slate-600">
                            <span>{m.etd || '--:--'} - {m.eta || '--:--'}</span>
                            <span>{m.bookingId || m.quoteId.slice(0, 8)}</span>
                          </div>
                          {renderInboundHubBadges(m)}
                          <div className="mt-auto flex justify-between items-center">
                            <div className="flex -space-x-3">
                              {m.assignedCrew?.map(id => {
                                const p = pilots.find(u => u.id === id);
                                const photoUrl = (p as any)?.photoUrl || (p as any)?.avatar || '';
                                const displayName = (p as any)?.name || `${(p as any)?.firstName || ''} ${(p as any)?.lastName || ''}`.trim() || (p as any)?.email || 'Crew';
                                const initial = displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : '?';
                                return (
                                  <div key={id} className="w-10 h-10 rounded-full border-4 border-white bg-slate-900 text-white text-[9px] font-black flex items-center justify-center shadow-lg relative group/pilot overflow-hidden" title={displayName}>
                                    {photoUrl ? (
                                      <img src={photoUrl} className="w-full h-full object-cover" alt={displayName} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    ) : null}
                                    {!photoUrl && initial}
                                    {isDFO && (
                                      <button
                                        onClick={() => onUnassignPilot(id, m.quoteId, m.id)}
                                        className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/pilot:opacity-100 transition-all scale-0 group-hover/pilot:scale-100"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                              {(!m.assignedCrew || m.assignedCrew.length === 0) && (
                                <div className="w-10 h-10 rounded-full border-4 border-white bg-slate-100 flex items-center justify-center text-slate-300">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4"/></svg>
                                </div>
                              )}
                            </div>
                            <span className="text-[10px] font-black text-indigo-600">{(m.assignedCrew || []).length} crew</span>
                          </div>
                          {!!m.assignedCrew?.length && (
                            <div className="mt-3 space-y-1">
                              {m.assignedCrew.map(id => {
                                const crew = pilots.find(u => u.id === id);
                                const displayName = (crew as any)?.name || `${(crew as any)?.firstName || ''} ${(crew as any)?.lastName || ''}`.trim() || (crew as any)?.email || 'Crew';
                                const roleCode = getCrewRoleCode(id);
                                const roleLabel =
                                  roleCode === 'CP'
                                    ? 'Chief Pilot'
                                    : roleCode === 'DFO'
                                    ? 'DFO / Pilot'
                                    : roleCode === 'FIRST_OFFICER'
                                    ? 'First Officer'
                                    : roleCode === 'PILOT'
                                    ? 'Pilot'
                                    : roleCode;
                                return (
                                  <div key={`${m.quoteId}-${m.id}-${id}`} className="text-[10px] font-semibold text-slate-600 flex items-center justify-between">
                                    <span className="truncate pr-2">{displayName}</span>
                                    <span className="text-[9px] uppercase tracking-wider text-slate-400">{roleLabel || 'Crew'}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {crewLegErrors[`${m.quoteId}:${m.id}`] && (
                            <p className="mt-2 text-[10px] font-semibold text-rose-600">
                              {crewLegErrors[`${m.quoteId}:${m.id}`]}
                            </p>
                          )}
                          {draggedPilotId && (
                            <div className="absolute inset-0 bg-indigo-600/5 rounded-2xl border-2 border-indigo-400 border-dashed animate-pulse flex items-center justify-center z-10">
                              <span className="text-[9px] font-black text-indigo-600 uppercase">Authorize Pilot Drop</span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
          <div className={`grid gap-4 ${tailViewMode === 'FOCUS' ? 'grid-cols-1' : 'grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3'}`}>
          {displayedTailEntries.map(({ tail, missions: rawGroup }) => {
            const group = sortMissions(collapseScheduledOpsLegs(rawGroup));
            const ac = aircrafts.find(a => a.tailNumber === tail);
            if (!ac) return null;

            const fuelTotal = (ac.fuelStatus?.leftWing || 0) + (ac.fuelStatus?.rightWing || 0);

            return (
              <div
                key={tail}
                ref={(el) => {
                  tailColumnRefs.current[tail] = el;
                }}
                onDragOver={e => e.preventDefault()}
                onDrop={() => onDropOnAircraft(tail)}
                className={`rounded-3xl border overflow-hidden flex flex-col bg-white shadow-lg min-h-[560px] ${
                  selectedTail === tail ? 'border-indigo-300' : 'border-slate-200'
                }`}
              >
                <div className={`sticky top-0 z-10 border-b p-4 ${viewMode === 'GROUND' ? 'border-emerald-700 bg-emerald-950/95 text-white' : 'border-slate-200 bg-white/95 text-slate-900'} backdrop-blur`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-3xl font-black tracking-tight font-mono uppercase">{tail}</h3>
                    <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                      viewMode === 'GROUND'
                        ? 'border-white/20 bg-white/10 text-white'
                        : 'border-indigo-200 bg-indigo-50 text-indigo-700'
                    }`}>
                      {group.length} leg{group.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    {viewMode === 'GROUND' ? (
                      <div className="flex-1">
                        <div className="mb-1 flex items-center justify-between text-[10px] font-black uppercase tracking-[0.12em] text-emerald-200">
                          <span>Onboard Fuel</span>
                          <span>{fuelTotal}L</span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.min((fuelTotal / 1500) * 100, 100)}%` }} />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Crew</span>
                        <div className="flex -space-x-2">
                          {Array.from(new Set(group.flatMap(m => m.assignedCrew || []))).slice(0, 5).map(id => {
                            const p = pilots.find(u => u.id === id);
                            const photoUrl = (p as any)?.photoUrl || (p as any)?.avatar || '';
                            const displayName = (p as any)?.name || `${(p as any)?.firstName || ''} ${(p as any)?.lastName || ''}`.trim() || (p as any)?.email || 'Crew';
                            const initial = displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : '?';
                            return (
                              <div key={id} className="h-8 w-8 overflow-hidden rounded-full border-2 border-white bg-indigo-600 text-[10px] font-black text-white flex items-center justify-center" title={displayName}>
                                {photoUrl ? <img src={photoUrl} className="h-full w-full object-cover" alt={displayName} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : initial}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {viewMode === 'GROUND' && (
                      <button onClick={() => onSetShowFuelModal(tail)} className="ml-3 rounded-xl bg-emerald-500 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-white hover:bg-emerald-400">Log Uplift</button>
                    )}
                  </div>
                </div>

                <div className={`flex-1 space-y-3 overflow-y-auto custom-scrollbar p-4 ${viewMode === 'GROUND' ? 'bg-emerald-50/30' : 'bg-slate-50/60'}`}>
                  {group.map(m => viewMode === 'CREW' ? renderCrewMissionCard(m) : (
                    <div
                      key={`${m.quoteId}-${m.id}`}
                      draggable={isFlightPlanner}
                      onDragStart={() => onDragMissionStart(m.source, m.quoteId, resolveMissionAssignmentId(m))}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.stopPropagation(); onDropPilotOnLeg(m.quoteId, m.id); }}
                      className={`min-h-[272px] bg-white rounded-xl p-4 border border-slate-200 shadow-sm transition-all group/card relative flex flex-col ${draggedPilotId ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-transparent' : 'hover:shadow-md cursor-grab active:cursor-grabbing'}`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.12em] border ${m.source === 'SCHEDULED' ? 'border-emerald-100 bg-emerald-50 text-emerald-600' : 'border-indigo-100 bg-indigo-50 text-indigo-600'}`}>
                          {m.source === 'SCHEDULED' ? 'SCHEDULED' : m.productMode}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onOpenPaxModal(m)}
                            className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.1em] border bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                            title="View passenger list"
                          >
                            Pax {m.paxCount ?? 0}
                          </button>
                          <PlanningWeatherNode code={m.to} />
                          {isFlightPlanner && (
                            <button
                              onClick={() => {
                                if (m.source === 'CHARTER') {
                                  onUpdateQuote?.(m.quoteId, { tailNumber: 'UNASSIGNED' });
                                } else if (m.bookingId) {
                                  onUnassignScheduledLeg(m.bookingId as string);
                                }
                              }}
                              className="text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover/card:opacity-100"
                              title="Unassign Sector"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <h5 className="mb-2 line-clamp-1 text-base font-black text-slate-800">{m.customerName}</h5>
                      <div className="mb-3 flex items-center gap-3 text-[11px] font-black uppercase text-slate-500">
                        <span>{m.from}</span>
                        <svg className="w-4 h-4 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                        <span>{m.to}</span>
                      </div>
                      <div className="mb-2 flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-slate-600">
                        <span>{m.etd || '--:--'} - {m.eta || '--:--'}</span>
                        <span>{m.bookingId || m.quoteId.slice(0, 8)}</span>
                      </div>
                      {renderInboundHubBadges(m)}
                      <div className="mt-auto flex justify-between items-center">
                        <div className="flex -space-x-3">
                          {m.assignedCrew?.map(id => {
                            const p = pilots.find(u => u.id === id);
                            const photoUrl = (p as any)?.photoUrl || (p as any)?.avatar || '';
                            const displayName = (p as any)?.name || `${(p as any)?.firstName || ''} ${(p as any)?.lastName || ''}`.trim() || (p as any)?.email || 'Crew';
                            const initial = displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : '?';
                            return (
                              <div key={id} className="w-10 h-10 rounded-full border-4 border-white bg-slate-900 text-white text-[9px] font-black flex items-center justify-center shadow-lg relative group/pilot overflow-hidden" title={displayName}>
                                {photoUrl ? (
                                  <img src={photoUrl} className="w-full h-full object-cover" alt={displayName} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                ) : null}
                                {!photoUrl && initial}
                                {isDFO && (
                                  <button
                                    onClick={() => onUnassignPilot(id, m.quoteId, m.id)}
                                    className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/pilot:opacity-100 transition-all scale-0 group-hover/pilot:scale-100"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          {(!m.assignedCrew || m.assignedCrew.length === 0) && (
                            <div className="w-10 h-10 rounded-full border-4 border-white bg-slate-100 flex items-center justify-center text-slate-300">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4"/></svg>
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] font-black text-indigo-600">{(m.assignedCrew || []).length} crew</span>
                      </div>
                      {!!m.assignedCrew?.length && (
                        <div className="mt-3 space-y-1">
                          {m.assignedCrew.map(id => {
                            const crew = pilots.find(u => u.id === id);
                            const displayName = (crew as any)?.name || `${(crew as any)?.firstName || ''} ${(crew as any)?.lastName || ''}`.trim() || (crew as any)?.email || 'Crew';
                            const roleCode = getCrewRoleCode(id);
                            const roleLabel =
                              roleCode === 'CP'
                                ? 'Chief Pilot'
                                : roleCode === 'DFO'
                                ? 'DFO / Pilot'
                                : roleCode === 'FIRST_OFFICER'
                                ? 'First Officer'
                                : roleCode === 'PILOT'
                                ? 'Pilot'
                                : roleCode;
                            return (
                              <div key={`${m.quoteId}-${m.id}-${id}`} className="text-[10px] font-semibold text-slate-600 flex items-center justify-between">
                                <span className="truncate pr-2">{displayName}</span>
                                <span className="text-[9px] uppercase tracking-wider text-slate-400">{roleLabel || 'Crew'}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {crewLegErrors[`${m.quoteId}:${m.id}`] && (
                        <p className="mt-2 text-[10px] font-semibold text-rose-600">
                          {crewLegErrors[`${m.quoteId}:${m.id}`]}
                        </p>
                      )}
                      {draggedPilotId && (
                        <div className="absolute inset-0 bg-indigo-600/5 rounded-2xl border-2 border-indigo-400 border-dashed animate-pulse flex items-center justify-center z-10">
                          <span className="text-[9px] font-black text-indigo-600 uppercase">Authorize Pilot Drop</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {group.length === 0 && (
                    <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white px-4 py-10 text-center">
                      <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Tail Idle</p>
                      <p className="mt-1 text-xs font-semibold text-slate-500">Drop a mission here from queue</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
          )}
          </div>
        </div>
      </div>
      )}

      {showFuelModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/80 backdrop-blur-xl p-6">
          <div className="bg-white rounded-[4rem] w-full max-w-xl overflow-hidden shadow-2xl animate-in zoom-in-95">
            <div className="p-10 bg-emerald-900 text-white flex justify-between items-center">
              <div>
                <h3 className="text-3xl font-black uppercase tracking-tight">Fuel Supply Protocol</h3>
                <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.3em] mt-2">Tail: {showFuelModal}</p>
              </div>
              <button onClick={() => onSetShowFuelModal(null)} className="text-white/40 hover:text-white transition-all">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="p-10 space-y-8">
              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Uplift Quantity (Liters)</label>
                <input
                  type="number"
                  className="w-full bg-slate-50 border-none rounded-3xl p-8 text-4xl font-black font-mono focus:ring-4 focus:ring-emerald-500/10 outline-none"
                  value={fuelLiters}
                  onChange={e => onSetFuelLiters(parseInt(e.target.value, 10) || 0)}
                />
              </div>
              <button onClick={onHandleFuelCommit} className="w-full py-8 bg-emerald-600 text-white rounded-[2.5rem] font-black text-[10px] uppercase tracking-[0.4em] hover:bg-emerald-500 transition-all shadow-xl">Commit Uplift to Registry</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlanningExecutionBoard;
